import React, {useEffect, useRef} from 'react';
import {Rating} from "@mui/material";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import toast, {Toaster} from "react-hot-toast";
import {useQuery} from "@tanstack/react-query";

import styles from './FeedbackPageCleaner.module.scss';
import AddPhotoFeedbackPage from "../../components/AddPhotoFeedbackPage/AddPhotoFeedbackPage";
import {feedbackCleanerActions} from "../../store/slices/feedbackCleanerSlice";
import MainButtonWithBackground from "../../components/UI/MainButtonWithBackground/MainButtonWithBackground";
import {feedbackService} from "../../services/feedback.service";
import {MAIN_ROUTES} from "../../routing/main_routes";
import MainButtonTransparentBackground
    from "../../components/UI/MainButtonTransparentBackground/MainButtonTransparentBackground";
import {qrCodeActions} from "../../store/slices/qrCodeSlice";
import {qrCodeService} from "../../services/qrCode.service";
import {resolveMediaUrl} from "../../configs/urls";
import {urlsToFiles} from "../../utils/urlsToFiles";
import IsCleaner from "../../HOC/IsCleaner";

const FeedbackPageCleaner = ({order, handleClose}) => {
    const navigate = useNavigate();

    const visibilityRef = useRef(null);

    const dispatch = useDispatch();
    const {
        feedback_text,
        client_assessment,
        communication_assessment,
        is_order_successful,
        photos_completed_order
    } = useSelector(state => state.feedbackCleaner);
    const {session_id, photos} = useSelector(state => state.qrCode);

    useEffect(() => {
        if (!is_order_successful) {
            dispatch(feedbackCleanerActions.setIsOrderSuccessful('Так'));
        }

        visibilityRef.current = document.visibilityState;

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                handleClose();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        const timer = setTimeout(() => {
            handleClose();
        }, 59 * 60 * 1000);

        return () => {
            handleClose();
            dispatch(feedbackCleanerActions.clearAllData());
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            clearTimeout(timer);
        }
    }, [])

    const turnReceivedImgUrlsToFiles = (photoUrls) =>
        urlsToFiles(photoUrls.map(resolveMediaUrl));

    const generateQrCode = async () => {
        try {
            await dispatch(qrCodeActions.generate());
        } catch (error) {
            console.error(error);
        }
    }

    const {data} = useQuery({
        // session_id in the key (re)starts the poll once the QR session exists;
        // ``enabled`` avoids running the query with a null session_id.
        queryKey: ['get-photos-qr-code', session_id],
        queryFn: () => qrCodeService.getPhotos(session_id),
        enabled: !!session_id,
        throwOnError: async (error) => {
            if (error?.response?.status === 404) {
                console.error('ERORR')
                dispatch(qrCodeActions.clearAllData());
                await generateQrCode();
                alert('Cесія завантаження фото за допомогою QR коду закінчилась! Спробуйте ще раз!');
            }
        },
        // Keep polling while no photos have arrived (length 0 or undefined).
        refetchInterval: photos?.length ? false : 3000,
        refetchIntervalInBackground: true
    });

    useEffect(() => {
        // Guard against overwriting the store with undefined, which would stop
        // the poll before any uploaded photo is fetched.
        if (data?.photos) {
            dispatch(qrCodeActions.setPhotos(data.photos));
        }
    }, [data, dispatch])

    const handleCreateFormData = (photos, feedback, qrCodePhotos) => {
        let formData = new FormData();

        if (photos?.length > 0) {
            for (const photo of photos) {
                formData.append('photos_data', photo);
            }
        }

        if (qrCodePhotos?.length > 0) {
            for (const photo of qrCodePhotos) {
                formData.append('photos_data', photo);
            }
        }

        formData.append('feedback', JSON.stringify(feedback));

        return formData;
    };

    useEffect(() => {
        return () => {
            dispatch(qrCodeActions.clearAllData());
        }
    }, []);

    const handleCreateFeedback = async () => {
        try {
            let isOrderSuccessfulBoolean = true;
            if (is_order_successful === 'Ні') {
                isOrderSuccessfulBoolean = false;
            }

            const feedback = {
                client_assessment,
                feedback_text,
                is_order_successful: isOrderSuccessfulBoolean,
                communication_assessment,
                order,
            }
            const files = await turnReceivedImgUrlsToFiles(photos);
            const formData = handleCreateFormData(photos_completed_order, feedback, files);

            await feedbackService.createForClient(formData);
            toast.success('Відгук успішно надіслано!', {id: "feedback-cleaner"});
            handleClose();
            setTimeout(() => {
                navigate(MAIN_ROUTES.ORDERS);
            }, 2000)
        } catch (error) {
            toast.error(`Відгук не вдалось надіслати. Спробуйте, будь ласка, ще раз!${JSON.stringify(error?.response?.data)}`, {id: "feedback-cleaner"});
        } finally {
            dispatch(feedbackCleanerActions.clearAllData());
            handleClose();
        }
    }

    const handleCancel = () => {
        handleClose(); // close feedback modal
    }

    console.log(order)

    return (
        <div className={styles.feedbackPageCleaner}>
            <div className={styles.feedbackPageCleaner_content}>
                <h3>Оцінка клієнта</h3>

                <div className={styles.feedbackPageCleaner_content_rating}>
                    <div className={styles.feedbackPageCleaner_content_rating_feedback}>
                        <p>Залишіть відгук про замовника <sup>*</sup></p>
                        <textarea
                            value={feedback_text}
                            onChange={(e) => dispatch(feedbackCleanerActions.setFeedbackText(e.target.value))}
                            placeholder={'Ваш відгук'}
                            rows="4"
                        ></textarea>
                    </div>

                    <div className={styles.feedbackPageCleaner_content_rating_star}>
                        <p>Оцініть замовника <sup>*</sup></p>
                        <Rating
                            style={{alignSelf: 'flex-start'}}
                            size="large"
                            name="simple-controlled"
                            value={client_assessment}
                            onChange={(event, newValue) => dispatch(feedbackCleanerActions.setClientAssessment(newValue))}
                        />
                    </div>

                    <div className={styles.feedbackPageCleaner_content_rating_star}>
                        <p>Оцініть комунікацію з замовником <sup>*</sup></p>
                        <Rating
                            style={{alignSelf: 'flex-start'}}
                            size="large"
                            name="simple-controlled"
                            value={communication_assessment}
                            onChange={(event, newValue) => dispatch(feedbackCleanerActions.setCommunicationAssessment(newValue))}
                        />
                    </div>

                    <div className={styles.feedbackPageCleaner_content_rating_star}>
                        <p>Чи успішно виконане замовлення? <sup>*</sup></p>
                        <div className={styles.feedbackPageCleaner_content_rating_star_buttons}>
                            <button
                                onClick={() => dispatch(feedbackCleanerActions.setIsOrderSuccessful('Так'))}
                                className={is_order_successful === 'Так' ? styles.active : ''}
                            >Так
                            </button>
                            <button
                                onClick={() => dispatch(feedbackCleanerActions.setIsOrderSuccessful('Ні'))}
                                className={is_order_successful === 'Ні' ? styles.active : ''}
                            >Ні
                            </button>
                        </div>
                    </div>
                </div>

                <AddPhotoFeedbackPage type={'cleaner'}/>

                <div className={styles.feedbackPageCleaner_content_buttons}>
                    <MainButtonTransparentBackground
                        onClick={handleCancel}
                        width={'auto'}
                        type={'button'}
                        condition={false}
                        text={'Скасувати'}
                    />
                    <MainButtonWithBackground
                        onClick={handleCreateFeedback}
                        width={'auto'}
                        type={'button'}
                        condition={(photos_completed_order?.length === 0 && photos?.length === 0) || (!client_assessment || !communication_assessment) || !feedback_text}
                        text={'Завершити Замовлення'}
                    />
                </div>
                {/*<button className={styles.feedbackPageCleaner_content_button}>Підтвердити</button>*/}

                {/* to appear hot toast notification */}
                {/*<Toaster/>*/}
            </div>
        </div>
    );
};

export default IsCleaner(FeedbackPageCleaner);
